import { useCallback, useRef } from "react";

/**
 * トラックパッドの横スワイプ（deltaX が主体の wheel イベント）で要素を
 * 確実に横スクロールさせるための ref callback を返す。
 *
 * 横にはみ出した overflow-x: auto のコンテナは本来ネイティブに横パンできるが、
 * Chrome はジェスチャ開始時点のスクローラにラッチするため、縦スクロールの
 * あるページ上では横スワイプがページ側に取られて効かないことがある。
 * deltaX 主体のときに自前で scrollLeft を進めて preventDefault することで、
 * カーソルが乗っているコンテナを常に優先して横パンできるようにする。
 *
 * preventDefault が必要なため passive: false で登録する（React の onWheel は
 * ルートで passive 登録されるので使えない）。返り値をそのまま ref に渡す。
 */
export function useHorizontalWheelPan<T extends HTMLElement>() {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((el: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      // 縦主体のジェスチャは通常のページスクロールに任せる
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      if (maxScrollLeft <= 0) return;
      el.scrollLeft = Math.max(
        0,
        Math.min(maxScrollLeft, el.scrollLeft + e.deltaX),
      );
      // 端に達した後も履歴スワイプ等の既定動作へ化けないよう消費する
      e.preventDefault();
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    cleanupRef.current = () => el.removeEventListener("wheel", handleWheel);
  }, []);
}
